import { deleteApp, initializeApp } from 'firebase/app';
import { createUserWithEmailAndPassword, initializeAuth, inMemoryPersistence } from 'firebase/auth';
import { firebaseConfig } from '../config/firebase';

const creationErrorMessage = (code: string): string | null => {
  switch (code) {
    case 'auth/error-code:-47':
    case 'auth/internal-error':
      return 'O Firebase não conseguiu concluir a criação do acesso. Aguarde alguns minutos e tente novamente. Se o erro persistir, verifique a resposta do Firebase e as configurações do projeto.';
    case 'auth/network-request-failed':
      return 'Não foi possível conectar ao Firebase. Verifique sua conexão e tente novamente.';
    case 'auth/too-many-requests':
      return 'O Firebase limitou temporariamente as tentativas. Aguarde alguns minutos antes de tentar novamente.';
    case 'auth/email-already-in-use':
      return 'Este e-mail já possui uma conta no Firebase, mas não foi encontrado no cadastro do RevaHub. Solicite a conferência do vínculo da conta antes de criar outro acesso.';
    case 'auth/weak-password':
      return 'A senha não atende aos requisitos do Firebase. Informe uma senha mais forte.';
    case 'auth/invalid-email':
      return 'Informe um endereço de e-mail válido.';
    default:
      return null;
  }
};

// Cria a conta em uma instância isolada e sem persistência de sessão no navegador.
// Não há retry automático: a criação pode ter sido processada mesmo após uma falha de rede.
export async function createFirebaseUser(email: string, password: string): Promise<string> {
  const appName = `user-creation-${globalThis.crypto.randomUUID()}`;
  const app = initializeApp(firebaseConfig, appName);
  try {
    const temporaryAuth = initializeAuth(app, { persistence: inMemoryPersistence });
    const { user } = await createUserWithEmailAndPassword(temporaryAuth, email, password);
    return user.uid;
  } catch (error: unknown) {
    const code = typeof error === 'object' && error !== null && 'code' in error
      ? String(error.code)
      : '';
    const message = creationErrorMessage(code);
    if (!message) throw error;
    throw Object.assign(new Error(message), {
      name: 'FirebaseUserCreationError',
      code,
      cause: error,
    });
  } finally {
    await deleteApp(app).catch(() => {
      console.warn('Não foi possível liberar a instância temporária de criação de usuário.');
    });
  }
}
